/**
 * TrashTalk — AI Trash Talk Generator (v2 redesign)
 *
 * Tone selector pills (Friendly / Spicy / Nuclear — active = gradient),
 * dark quote card with serif open-quote and mascot attribution,
 * Copy + "Another one" actions, and the intel panel JAI wrote from.
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Page, V2Card, PrimaryBtn, SecondaryBtn } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'

// ── Preview flag ───────────────────────────────────────────────────────────────
const IS_PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === '1'

// ── Mock data ──────────────────────────────────────────────────────────────────
const MY_TEAM = 'Rim Reapers'
const OPP_TEAM = 'Dunks & Dimes'

// Tone: 0 = friendly, 1 = spicy, 2 = nuclear
const TONES = ['Friendly', 'Spicy', 'Nuclear']

const TRASH_LINES = {
  0: [
    `Hey ${OPP_TEAM} — respect the grind this season, genuinely. But you're facing ${MY_TEAM} this week, and I've been saving something special for you. Good luck, you'll need it.`,
    `${OPP_TEAM}, love the effort, love the energy. The five-game losing streak you're about to walk into? That one might sting. Best of luck this week.`,
    `Just want you to know: I've enjoyed watching you fight for your playoff life. The fight ends this week though. See you in the final standings somewhere around 5th.`,
  ],
  1: [
    `${OPP_TEAM}, your team is held together with duct tape and a prayer. Bam Adebayo's 2.3 assists per game is carrying your entire assist category. I eat offenses like yours for breakfast and I'm particularly hungry this week.`,
    `Checked your lineup. Three of your guys are listed as questionable, your waiver wire ace just got traded, and you're facing the team that leads the league in every category except Bench Regret. Sleep well.`,
    `Your franchise player averaged 18 points in his last 5. That's cute. My worst scorer averages 19. Come ready.`,
  ],
  2: [
    `Let me be honest: I ran the simulations. 10,000 times. You won 212. And 67 of those were because my entire roster got food poisoning from the same restaurant on a Tuesday. ${MY_TEAM} doesn't lose to ${OPP_TEAM}. It's not personal — it's just math.`,
    `Your team photo should come with a white flag. You haven't beaten a top-3 team in 8 weeks, your streaming strategy is picking up players I cut in week 2, and your star is averaging fewer points than my bench. But please — keep talking. It makes the win taste better.`,
    `I've studied the tape. I've run the numbers. I've read JAI's full scouting report on your franchise. The verdict? Your best case scenario this week is losing by under 20. I'll see you on the other side.`,
  ],
}

const INTEL = [
  { label: "Their record this season", value: "7-7 — scraping into playoffs" },
  { label: "Your H2H record vs them", value: "8 wins, 3 losses — you dominate" },
  { label: "Their weakest category", value: "Rebounds (#8 in league)" },
  { label: "Their best player last week", value: "Bam Adebayo — 32.4 pts" },
  { label: "Your current streak", value: "W5 — hottest team in the league" },
]

const copyIcon = (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <rect x="4.5" y="4.5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9.5 4.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" stroke="currentColor" strokeWidth="1.4" />
  </svg>
)
const refreshIcon = (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M12 7a5 5 0 1 1-1.5-3.6M12 1v3h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

// ── Main ───────────────────────────────────────────────────────────────────────

export default function TrashTalk() {
  const { id } = useParams()
  const [league, setLeague] = useState(IS_PREVIEW ? { name: 'The Hardwood Cartel' } : null)
  const [loading, setLoading] = useState(!IS_PREVIEW)
  const [tone, setTone] = useState(1)
  const [lineIndex, setLineIndex] = useState(0)
  const [copied, setCopied] = useState(false)
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    if (IS_PREVIEW) return
    leaguesApi.get(id).then(r => {
      setLeague(r.data.league)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [id])

  const lines = TRASH_LINES[tone]
  const currentLine = lines[lineIndex % lines.length]

  function regenerate() {
    setGenerating(true)
    setTimeout(() => {
      setLineIndex(i => i + 1)
      setGenerating(false)
    }, 800)
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(currentLine).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <span className="text-sm" style={{ color: V2.faint }}>Loading…</span>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <V2Page
        kicker={
          <Link to={`/league/${id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {league?.name ? `${league.name} · vs ${OPP_TEAM}` : `vs ${OPP_TEAM}`}
          </Link>
        }
        title="Trash Talk"
        sub="JAI writes the smack, armed with their actual stats. You take the credit."
        maxWidth={720}
      >

        {/* ── Tone selector pills ───────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
          {TONES.map((label, i) => {
            const active = tone === i
            return (
              <button
                key={label}
                onClick={() => { setTone(i); setLineIndex(0) }}
                style={{
                  fontSize: 13, fontWeight: 700, borderRadius: 999, padding: '8px 18px',
                  background: active ? V2.gradient : 'white',
                  color: active ? 'white' : V2.mute,
                  border: active ? 'none' : `1px solid ${V2.hair}`,
                  boxShadow: active ? V2.ctaShadow : 'none',
                  cursor: 'pointer', fontFamily: JAI_FONTS.tight, transition: 'all 180ms',
                }}
              >
                {label}
              </button>
            )
          })}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: V2.faint }}>Sourced from their real box scores</span>
        </div>

        {/* ── Dark quote card ───────────────────────────────────────────── */}
        <div style={{ background: V2.darkHero, borderRadius: 18, padding: '26px 30px', color: 'white', position: 'relative', overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ position: 'absolute', right: -50, bottom: -70, width: 220, height: 220, borderRadius: '50%', background: 'rgba(58,124,255,0.09)' }} />
          <div style={{ fontSize: 44, fontWeight: 800, color: V2.blueBright, lineHeight: 0.6, fontFamily: 'Georgia, serif', marginBottom: 6 }}>“</div>
          {generating ? (
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.45)', margin: '0 0 16px', position: 'relative', minHeight: 56 }} className="animate-pulse">
              JAI is cooking…
            </p>
          ) : (
            <p style={{ fontSize: 19, fontWeight: 600, lineHeight: 1.5, margin: '0 0 16px', letterSpacing: '-0.01em', position: 'relative' }}>
              {currentLine}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
            <JAIMascot size={30} state={generating ? 'thinking' : 'idle'} animate={generating} />
            <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.5)', fontFamily: JAI_FONTS.mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              JAI · {TONES[tone]} mode · Draft {(lineIndex % lines.length) + 1} of {lines.length}
            </span>
          </div>
        </div>

        {/* ── Actions ───────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 26 }}>
          <PrimaryBtn onClick={copyToClipboard} style={{ borderRadius: 11, padding: '10px 18px', fontSize: 13 }}>
            {copyIcon} {copied ? 'Copied!' : 'Copy to clipboard'}
          </PrimaryBtn>
          <SecondaryBtn onClick={regenerate} disabled={generating} style={{ borderRadius: 11, padding: '10px 18px', fontSize: 13 }}>
            {refreshIcon} Another one
          </SecondaryBtn>
        </div>

        {/* ── Intel panel ───────────────────────────────────────────────── */}
        <V2Card pad="18px 20px">
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.faint, fontFamily: JAI_FONTS.mono, marginBottom: 10 }}>
            Intel JAI used to write this
          </div>
          {INTEL.map((item, i) => (
            <div key={item.label} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16,
              padding: '8px 0', borderBottom: i < INTEL.length - 1 ? '1px solid rgba(10,31,68,0.06)' : 'none',
            }}>
              <span style={{ fontSize: 12.5, color: V2.mute }}>{item.label}</span>
              <span style={{ fontSize: 12.5, color: V2.navy, fontWeight: 600, textAlign: 'right' }}>{item.value}</span>
            </div>
          ))}
        </V2Card>

      </V2Page>
    </AppShell>
  )
}
